import { z } from "zod";

export const QaDelegatedPayerFixtureRecipeIdSchema = z.enum([
  "int955_hosted_delegated_payer_fixtures_v1",
  "int957_hosted_staff_payer_fixtures_v1",
]);

export const QaDelegatedPayerFixtureRecipeSchema = z.discriminatedUnion(
  "recipeId",
  [
    z.object({
      recipeId: z.literal("int955_hosted_delegated_payer_fixtures_v1"),
      auditEntityType: z.literal("qa_int955_delegated_payer_fixture"),
      receiptNamespace: z.literal("int955-delegated-payers"),
      confirmationLabel: z.literal("INT-955 DELEGATED PAYER FIXTURES"),
    }).strict(),
    z.object({
      recipeId: z.literal("int957_hosted_staff_payer_fixtures_v1"),
      auditEntityType: z.literal("qa_int957_staff_payer_fixture"),
      receiptNamespace: z.literal("int957-staff-payers"),
      confirmationLabel: z.literal("INT-957 STAFF PAYER FIXTURES"),
    }).strict(),
  ],
);

export type QaDelegatedPayerFixtureRecipe = z.infer<
  typeof QaDelegatedPayerFixtureRecipeSchema
>;

export const QA_DELEGATED_PAYER_FIXTURE_RECIPES = {
  int955: QaDelegatedPayerFixtureRecipeSchema.parse({
    recipeId: "int955_hosted_delegated_payer_fixtures_v1",
    auditEntityType: "qa_int955_delegated_payer_fixture",
    receiptNamespace: "int955-delegated-payers",
    confirmationLabel: "INT-955 DELEGATED PAYER FIXTURES",
  }),
  int957: QaDelegatedPayerFixtureRecipeSchema.parse({
    recipeId: "int957_hosted_staff_payer_fixtures_v1",
    auditEntityType: "qa_int957_staff_payer_fixture",
    receiptNamespace: "int957-staff-payers",
    confirmationLabel: "INT-957 STAFF PAYER FIXTURES",
  }),
} as const;

const FixtureFamilySchema = z.object({
  slot: z.enum(["a", "b"]),
  label: z.string().min(1),
  applicantId: z.string().uuid(),
  familyId: z.string().uuid(),
  ledgerEntryId: z.string().uuid(),
  openBalanceCents: z.number().int().positive(),
  assignmentCount: z.literal(0),
  assignmentDigest: z.string().min(1),
});

export const QaDelegatedPayerFixtureBlockerReasonSchema = z.enum([
  "fixture_identity_conflict",
  "payer_assignments_present",
  "retained_academic_year_balance_not_zero",
]);

export type QaDelegatedPayerFixtureBlockerReason = z.infer<
  typeof QaDelegatedPayerFixtureBlockerReasonSchema
>;

const FixturePreviewFamilySchema = FixtureFamilySchema.omit({
  familyId: true,
  ledgerEntryId: true,
}).extend({
  assignmentCount: z.number().int().nonnegative(),
  lifecycle: z.enum(["new", "retained", "conflict"]).optional(),
  blockerReasons: z.array(QaDelegatedPayerFixtureBlockerReasonSchema).optional(),
});

export const QaDelegatedPayerFixturePreviewInputSchema = z.object({
  schoolId: z.string().min(1),
  tenantSlug: z.string().min(1),
  releaseEvidenceId: z.string().min(1).max(200),
  externallyVerifiedDeployedSha: z.string().regex(/^[a-f0-9]{40}$/i),
  cleanupOwner: z.string().min(3).max(200),
});

export const QaDelegatedPayerFixturePreviewSchema = z.object({
  recipeId: QaDelegatedPayerFixtureRecipeIdSchema,
  environment: z.string().min(1),
  orgId: z.string().uuid(),
  schoolId: z.string().min(1),
  tenantSlug: z.string().min(1),
  releaseEvidenceId: z.string().min(1),
  externallyVerifiedDeployedSha: z.string().regex(/^[a-f0-9]{40}$/i),
  cleanupOwner: z.string().min(3),
  guardianEmailDigest: z.string().min(1),
  academicYear: z.string().min(1),
  targetOpenBalanceCents: z.number().int().positive(),
  families: z.array(FixturePreviewFamilySchema).length(2),
  canApply: z.boolean(),
  beforeStateDigest: z.string().min(1),
  confirmationText: z.string().min(1),
  releaseBinding: z.literal("externally_verified_release_preflight"),
});

export const QaDelegatedPayerFixtureApplyInputSchema =
  QaDelegatedPayerFixturePreviewInputSchema.extend({
    expectedBeforeStateDigest: z.string().min(1),
    confirmationText: z.string().min(1),
  });

export const QaDelegatedPayerFixtureApplyResultSchema = z.object({
  status: z.enum(["applied", "already_applied", "reactivated"]),
  preview: QaDelegatedPayerFixturePreviewSchema,
  families: z.array(FixtureFamilySchema).length(2),
  receiptReference: z.string().min(1),
  rollbackToken: z.string().min(1),
  rollbackConfirmationText: z.string().min(1),
  afterStateDigest: z.string().min(1),
  appliedAt: z.string().min(1),
  releaseBinding: z.literal("externally_verified_release_preflight"),
});

export const QaDelegatedPayerFixtureRollbackInputSchema = z.object({
  schoolId: z.string().min(1),
  tenantSlug: z.string().min(1),
  releaseEvidenceId: z.string().min(1).max(200),
  externallyVerifiedDeployedSha: z.string().regex(/^[a-f0-9]{40}$/i),
  rollbackToken: z.string().min(1),
  expectedStateDigest: z.string().min(1),
  confirmationText: z.string().min(1),
});

export const QaDelegatedPayerFixtureRollbackResultSchema = z.object({
  status: z.enum(["rolled_back", "already_rolled_back"]),
  receiptReference: z.string().min(1),
  families: z.array(z.object({
    slot: z.enum(["a", "b"]),
    familyId: z.string().uuid(),
    originalLedgerEntryId: z.string().uuid(),
    rollbackLedgerEntryId: z.string().uuid(),
    assignmentCount: z.literal(0),
    assignmentDigest: z.string().min(1),
  })).length(2),
  afterStateDigest: z.string().min(1),
  rolledBackAt: z.string().min(1),
  releaseBinding: z.literal("externally_verified_release_preflight"),
});

export type QaDelegatedPayerFixturePreview = z.infer<typeof QaDelegatedPayerFixturePreviewSchema>;
export type QaDelegatedPayerFixtureApplyResult = z.infer<typeof QaDelegatedPayerFixtureApplyResultSchema>;
export type QaDelegatedPayerFixtureRollbackResult = z.infer<typeof QaDelegatedPayerFixtureRollbackResultSchema>;
